import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import jwtDecode from 'jwt-decode';
import { DataStorageService } from '@core/auth/services/data-storage.service';
import { Token } from '@core/auth/models/token';
import { ViewAsSessionService } from '@core/auth/view-as/view-as-session.service';

@UntilDestroy()
@Component({
  selector: 'ac-view-as-banner',
  imports: [TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './view-as-banner.component.html',
  styleUrl: './view-as-banner.component.scss'
})
export class ViewAsBannerComponent implements OnInit, OnDestroy {
  readonly bodyBannerVisibleClass: string = 'view-as-banner-active';

  visible: boolean = false;
  fullName: string = '';
  email: string = '';

  constructor(
    private readonly dataStorage: DataStorageService,
    private readonly viewAsSession: ViewAsSessionService,
    private readonly router: Router,
    private readonly cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.resolveVisibility();
    this.listenToNavigation();
  }

  ngOnDestroy(): void {
    document.body.classList.remove(this.bodyBannerVisibleClass);
  }

  exitViewAs(): void {
    this.viewAsSession.exitViewAs();
  }

  private listenToNavigation(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe(() => this.resolveVisibility());
  }

  // Re-run on every NavigationEnd (not just ngOnInit): the entry flow mounts this component
  // before the handoff token/marker exist, then soft-navigates to /home once they're set.
  private resolveVisibility(): void {
    const token: string = this.dataStorage.getToken();
    if (!this.dataStorage.isViewAsSession() || !token) {
      this.hideBanner();
      return;
    }
    let decoded: Token;
    try {
      decoded = jwtDecode(token);
    } catch {
      this.hideBanner();
      return;
    }
    this.fullName = decoded.fullName ?? '';
    // sub is the login (a sanitized email derivative for advocates); prefer the real address
    this.email = decoded.email ?? decoded.sub;
    this.visible = true;
    document.body.classList.add(this.bodyBannerVisibleClass);
    this.cd.markForCheck();
  }

  private hideBanner(): void {
    this.visible = false;
    document.body.classList.remove(this.bodyBannerVisibleClass);
    this.cd.markForCheck();
  }
}
